import React from "react";
import ReactDOM from "react-dom";
import config from "./config.js";
import Home from "./components/Home";
import Admin from "./components/Admin";
import LoginUser from "./components/LoginUser";
import Options from "./components/Options";
import Cancel from "./components/Cancel";
import moment from "moment-timezone";
import Error404 from "./components/Error404";
import WaitingRoom from "./components/WaitingRoom";
import PatientsList from "./components/PatientsList";
import ErrorBoundary from "./components/ErrorBoundary";

import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import { ApolloClient } from "apollo-client";
import { ApolloProvider } from "react-apollo";
import { createHttpLink } from "apollo-link-http";
import { ApolloLink, split } from "apollo-link";
import { InMemoryCache } from "apollo-cache-inmemory";
import { getMainDefinition } from "apollo-utilities";
import { withClientState } from "apollo-link-state";
import { CachePersistor } from "apollo-cache-persist";

import "./styles/styles.css"; // assuming a styles directory under src/

import { WebSocketLink } from "apollo-link-ws";
moment.tz.setDefault("Europe/Luxembourg");
moment.locale("fr");

const API_SIMPLE = config.apiSimple;
const API_WS = config.apiWS;

const wsLink = new WebSocketLink({
  uri: `${API_WS}`,
  options: {
    reconnect: true,
    inactivityTimeout: 0,
    timeout: 30000, //30sec
  },
});
wsLink.subscriptionClient.maxConnectTimeGenerator.duration = () =>
  wsLink.subscriptionClient.maxConnectTimeGenerator.max;

wsLink.subscriptionClient.onDisconnected(() => {
  console.log("Disconnected from WS link");
});
wsLink.subscriptionClient.onReconnected(() => {
  console.log("Reconnected to WS link");
});
wsLink.subscriptionClient.onError((error) => {
  console.log("Error on WS link", error);
});

const httpLink = createHttpLink({
  uri: `${API_SIMPLE}`,
});

const middlewareLink = new ApolloLink((operation, forward) => {
  const token = localStorage.getItem("graphcoolToken");
  const authorizationHeader = token ? `Bearer ${token}` : null;
  operation.setContext({
    headers: {
      authorization: authorizationHeader,
    },
  });
  return forward(operation);
});

const httpLinkWithAuthToken = middlewareLink.concat(httpLink);
const cache = new InMemoryCache().restore(window.__APOLLO_STATE__);

const stateLink = withClientState({
  cache,
  resolvers: {
    Mutation: {
      updateHistoryModal: (
        _,
        { isOpenHistoryModal, idPatientToShow },
        { cache }
      ) => {
        const data = {
          patientHistoryModal: {
            __typename: "PatientHistoryModal",
            isOpenHistoryModal,
            idPatientToShow,
          },
        };
        cache.writeData({ data });
        return data.patientHistoryModal;
      },
      updateDeleteModalOpen: (
        _,
        { isOpenDeleteModal, idPatientToDelete },
        { cache }
      ) => {
        const data = {
          patientDeleteModal: {
            __typename: "PatientDeleteModal",
            isOpenDeleteModal,
            idPatientToDelete,
          },
        };
        cache.writeData({ data });
        return data.patientDeleteModal;
      },
      updateRDVSelectTypeDoc: (
        _,
        { selectedRdvTypeId, selectedRdvTypeDuration, selectedDoctorId },
        { cache }
      ) => {
        const data = {
          rdvSelect: {
            __typename: "RDVSelect",
            selectedRdvTypeId,
            selectedRdvTypeDuration,
            selectedDoctorId,
          },
        };
        cache.writeData({ data });
        return data.rdvSelect;
      },
      updateRDVSelect: (
        _,
        {
          selectedDate,
          selectedPatientId,
          selectedRdvTypeId,
          selectedDoctorId,
          selectedView,
          selectedRdvTypeDuration,
          fromCache,
        },
        { cache }
      ) => {
        const data = {
          rdvSelect: {
            __typename: "RDVSelect",
            selectedDate,
            selectedPatientId,
            selectedRdvTypeId,
            selectedDoctorId,
            selectedView,
            selectedRdvTypeDuration,
            fromCache,
          },
        };
        cache.writeData({ data });
        return data.rdvSelect;
      },
      updateSortedDocs: (_, { sortedDocs }, { cache }) => {
        const data = {
          sortedDocs: {
            __typename: "SortedDocs",
            sortedDocs,
          },
        };
        cache.writeData({ data });
        return data.sortedDocs;
      },
    },
  },
  defaults: {
    rdvSelect: {
      __typename: "RDVSelect",
      selectedDate: moment().valueOf(),
      selectedPatientId: null,
      selectedRdvTypeId: null,
      selectedDoctorId: null,
      selectedView: "week",
      selectedRdvTypeDuration: null,
      fromCache: true,
    },
    patientDeleteModal: {
      __typename: "PatientDeleteModal",
      idPatientToDelete: null,
      isOpenDeleteModal: false,
    },
    patientHistoryModal: {
      __typename: "patientHistoryModal",
      idPatientToShow: null,
      isOpenHistoryModal: false,
    },
    sortedDocs: {
      __typename: "sortedDocs",
      sortedDocs: [],
      fromCache: true,
    },
  },
});

const link = split(
  ({ query }) => {
    const { kind, operation } = getMainDefinition(query);
    return kind === "OperationDefinition" && operation === "subscription";
  },
  wsLink,
  httpLinkWithAuthToken
);

const defaultOptions = {
  watchQuery: {
    fetchPolicy: "network-only",
    errorPolicy: "all",
  } /*fucks up apollo-link-state in network-only*/,
  query: {
    fetchPolicy: "network-only",
    errorPolicy: "all",
  },
};

const client = new ApolloClient({
  cache,
  link: ApolloLink.from([stateLink, link]),
  defaultOptions: defaultOptions,
});

client.onResetStore(stateLink.writeDefaults);

const persistor = new CachePersistor({
  cache,
  storage: window.localStorage,
  debug: false,
});

//persistor.purge();
persistor.restore();

/*
const client = new ApolloClient({
  link: link,
  cache: cache
});*/

ReactDOM.render(
  <ErrorBoundary>
    <ApolloProvider client={client}>
      <Router>
        <div>
          <Switch>
            <Route exact path="/" component={Home} />
            <Route exact path="/patient/:patientId" component={Home} />
            <Route path="/options/patient/:patientId" component={Options} />
            <Route path="/options" component={Options} />
            <Route path="/salleattente" component={WaitingRoom} />
            <Route
              path="/salleattente/patient/:patientId"
              component={WaitingRoom}
            />
            <Route path="/patients" component={PatientsList} />
            <Route
              path="/patients/patient/:patientId?"
              component={PatientsList}
            />
            <Route path="/admin" component={Admin} />
            <Route path="/login" component={LoginUser} />
            <Route path="/cancel/:lang/:cancelationHash" component={Cancel} />

            <Route component={Error404} />
          </Switch>
        </div>
      </Router>
    </ApolloProvider>
  </ErrorBoundary>,
  document.getElementById("root")
);



// WEBPACK FOOTER //
// ./src/index.js